/**
 * ScheduleDataEntry — Per-project wrapper around MetricEntryGrid for Schedule
 * percent-complete data entry.
 *
 * Mirrors MissionDataEntry's shape so the three functional-area wrappers
 * compose uniformly inside the Action Center Data tab (DEV-681 Phase 2).
 *
 * Inputs:
 *   - projectGuid                  Project UUID for the API write.
 *   - scheduleInstances            Schedule SubKpiInstance[] from kpiConfigs.
 *                                  Each becomes one row in the grid.
 *   - latestPointsByInstance       Map of instance_guid (UPPER) → latest
 *                                  ScheduleSeriesPointResponse for the
 *                                  "Current value" display column. Optional;
 *                                  rows render with `—` when missing.
 *   - canEdit                      Permission check from the host.
 *   - onDataSaved                  Optional callback fired after a successful
 *                                  save so the host can bump its data version.
 *
 * Save handler:
 *   Uses `batchUpsertScheduleSeriesPoints` rather than the create+409→update
 *   pattern that MissionDataEntry inherited from its older API surface.
 *   The schedule batch endpoint upserts on (project_id, instance_guid,
 *   reporting_date) so a single call handles both insert and update cases
 *   atomically — cleaner failure semantics for per-grid Save.
 *
 * Cache invalidation:
 *   Dispatches CACHE_UPDATE_EVENT with QUERY_KEYS.schedule so
 *   `useApiScheduleData` re-fetches immediately. The Data Reliability engine
 *   re-classifies the affected instance from due/overdue → on-time on the
 *   next render and the row falls out of the Action Center Data tab.
 */

import React, { useCallback, useMemo } from 'react';
import MetricEntryGrid, {
  type MetricRowDef,
  type MetricEntrySavePayload,
} from './MetricEntryGrid';
import MetricEntryStacked from './MetricEntryStacked';
import {
  batchUpsertScheduleSeriesPoints,
  type ScheduleSeriesPointResponse,
} from '../../api/scheduleSeriesPoints';
import type { SubKpiInstance } from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { invalidateAndRefetch, QUERY_KEYS } from '../../api/queryCache';

// ── Types ───────────────────────────────────────────────────

export interface ScheduleDataEntryProps {
  /** Project GUID for the API write. */
  projectGuid: string;
  /** Schedule sub-KPI instances configured on this project. */
  scheduleInstances: SubKpiInstance[];
  /**
   * Latest schedule series point per instance, keyed by instance_guid
   * (upper-cased). Populates the Current column. Missing entries render
   * as `—`, which is fine — the grid still accepts entry.
   */
  latestPointsByInstance?: Record<string, ScheduleSeriesPointResponse>;
  /** Whether the current user has edit permission. */
  canEdit: boolean;
  /** Called after a successful save so the host can refresh data. Receives
   *  save coverage so the host can tell a FULL save from a PARTIAL one — the
   *  Action Center only resolves the data item on full coverage (DEV-1256). */
  onDataSaved?: (info?: { savedCount: number; totalCount: number }) => void;
  /** Optional title override; defaults to the translated "Enter Schedule Progress". */
  title?: string;
  /** Layout variant: "grid" (wide, default) or "stacked" (narrow tray). */
  variant?: 'grid' | 'stacked';
}

// ── Component ───────────────────────────────────────────────

const ScheduleDataEntry: React.FC<ScheduleDataEntryProps> = ({
  projectGuid,
  scheduleInstances,
  latestPointsByInstance,
  canEdit,
  onDataSaved,
  title,
  variant = 'grid',
}) => {
  const { t } = useAppTranslation('kpi');

  // Build metric rows: one per configured schedule instance.
  const metrics: MetricRowDef[] = useMemo(() => {
    if (!Array.isArray(scheduleInstances) || scheduleInstances.length === 0) {
      return [];
    }
    return scheduleInstances.map((inst) => {
      const guid = String(inst?.instance_guid || '').toUpperCase();
      const latest = latestPointsByInstance?.[guid];
      return {
        key: guid,
        label: String(inst?.display_name || guid),
        unit: '%',
        currentValue: latest?.actualComplete ?? null,
        // Schedule percent-complete is bounded 0–100; "100" is the implicit
        // target on every instance. Showing it gives the user the same
        // "where am I going" anchor that Mission's targetValue provides.
        targetValue: 100,
        direction: 'increase_is_good' as const,
        valueType: 'percentage' as const,
      };
    });
  }, [scheduleInstances, latestPointsByInstance]);

  // Save handler — single batch upsert. Fails or succeeds atomically per grid.
  const handleSave = useCallback(
    async (entries: MetricEntrySavePayload[]) => {
      if (entries.length === 0) return;
      await batchUpsertScheduleSeriesPoints(
        entries.map((entry) => ({
          projectId: projectGuid,
          instanceGuid: entry.key,
          reportingDate: entry.reportingDate,
          actualComplete: entry.value,
        })),
      );

      // Bump the schedule cache so re-fetch happens before the next render.
      // invalidateAndRefetch (not raw dispatchEvent) — see queryCache.ts
      // comment about why subscribers don't refetch on a bare event.
      invalidateAndRefetch(QUERY_KEYS.schedule);

      if (onDataSaved) onDataSaved({ savedCount: entries.length, totalCount: metrics.length });
    },
    [projectGuid, onDataSaved, metrics.length],
  );

  // Hide entirely when there's nothing to enter or the user can't edit.
  // Cleaner than rendering an empty grid stub in the Action Center surface.
  if (metrics.length === 0 || !canEdit) return null;

  const Comp = variant === 'stacked' ? MetricEntryStacked : MetricEntryGrid;
  return (
    <Comp
      metrics={metrics}
      onSave={handleSave}
      readOnly={!canEdit}
      title={title || t('dataEntry.scheduleTitle') || 'Enter Schedule Progress'}
    />
  );
};

export default ScheduleDataEntry;
